import { useTheme } from '../contexts/ThemeContext'
import { useLang } from '../contexts/LangContext'
import type { Lang } from '../contexts/LangContext'

const LANGS: { value: Lang; flag: string; label: string }[] = [
  { value: 'pt', flag: '🇧🇷', label: 'Português' },
  { value: 'en', flag: '🇺🇸', label: 'English' },
  { value: 'es', flag: '🇪🇸', label: 'Español' },
]

interface Props {
  /** 'navbar' = inline row, 'fixed' = positioned top-right (for standalone use) */
  variant?: 'navbar' | 'fixed'
  /** Hide the theme toggle — use on pages with fixed visual identity (e.g. Landing) */
  hideTheme?: boolean
}

export function LangThemePicker({ variant = 'navbar', hideTheme = false }: Props) {
  const { resolvedTheme, toggle } = useTheme()
  const { lang, setLang, t } = useLang()
  const isDark = resolvedTheme === 'dark'

  const MoonIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
    </svg>
  )

  const SunIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
    </svg>
  )

  const btnBase: React.CSSProperties = {
    backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
    color: isDark ? '#D1D5DB' : '#374151',
  }

  const activeFlagStyle: React.CSSProperties = {
    backgroundColor: 'rgba(var(--brand-rgb), 0.2)',
    border: '1px solid rgba(var(--brand-rgb), 0.3)',
    outline: 'none',
  }

  const wrapper: React.CSSProperties = variant === 'fixed'
    ? { position: 'fixed', top: 12, right: 12, zIndex: 50, display: 'flex', alignItems: 'center', gap: 4 }
    : { display: 'flex', alignItems: 'center', gap: 4 }

  return (
    <div style={wrapper} role="group" aria-label={t('theme.pickerAriaLabel')}>
      {/* Flag buttons */}
      {LANGS.map(({ value, flag, label }) => (
        <button
          key={value}
          onClick={() => setLang(value)}
          title={label}
          aria-pressed={lang === value}
          className="rounded-md px-1.5 py-1 text-sm leading-none transition-all hover:scale-105 active:scale-95"
          style={lang === value ? activeFlagStyle : btnBase}
        >
          {flag}
        </button>
      ))}

      {!hideTheme && (
        <>
          {/* Divider */}
          <span style={{ width: 1, height: 16, backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)', marginLeft: 2, marginRight: 2 }} />

          {/* Theme toggle */}
          <button
            onClick={toggle}
            title={isDark ? t('theme.switchToLight') : t('theme.switchToDark')}
            className="rounded-md p-1.5 transition-all hover:scale-105 active:scale-95"
            style={btnBase}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>
        </>
      )}
    </div>
  )
}
